import React, { useState, useEffect } from "react";

export default function ThresholdAdjuster({ onChange }) {
	// Local state for thresholds as strings (to allow partial inputs)
	const [redThreshold, setRedThreshold] = useState("0.7");
	const [yellowThreshold, setYellowThreshold] = useState("0.4");
	const [warning, setWarning] = useState("");

	// Update parent on valid change
	const updateParent = (r, y) => {
		if (onChange) onChange({ redThreshold: r, yellowThreshold: y });
	};

	// Validate and update red threshold
	const handleRedChange = (e) => {
		let val = e.target.value;
		if (/^(\d*\.?\d*)$/.test(val)) {
			// Convert to float or 0
			let numVal = parseFloat(val);
			if (isNaN(numVal)) numVal = 0;

			// Clamp red to be >= yellow
			let yellowNum = parseFloat(yellowThreshold);
			if (isNaN(yellowNum)) yellowNum = 0;
			if (numVal < yellowNum) {
				numVal = yellowNum;
			}

			setRedThreshold(numVal.toString());
			setWarning("");
			updateParent(numVal, yellowNum);
		}
	};

	// Validate and update yellow threshold
	const handleYellowChange = (e) => {
		let val = e.target.value;
		if (/^(\d*\.?\d*)$/.test(val)) {
			let numVal = parseFloat(val);
			if (isNaN(numVal)) numVal = 0;

			// Clamp yellow to be <= red
			let redNum = parseFloat(redThreshold);
			if (isNaN(redNum)) redNum = 1;
			if (numVal > redNum) {
				numVal = redNum;
			}

			setYellowThreshold(numVal.toString());
			setWarning("");
			updateParent(redNum, numVal);
		}
	};

	// Effect to show warning if red < yellow (should not happen due to clamping, but just in case)
	useEffect(() => {
		let r = parseFloat(redThreshold);
		let y = parseFloat(yellowThreshold);
		if (!isNaN(r) && !isNaN(y) && r < y) {
			setWarning(
				"Warning: Duplicate threshold should be greater than or equal to Needs Review threshold."
			);
		} else {
			setWarning("");
		}
	}, [redThreshold, yellowThreshold]);

	return (
		<section
			style={{
				maxWidth: 360,
				margin: "2rem auto",
				padding: "1.5rem 2rem",
				borderRadius: 12,
				boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
				fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
				backgroundColor: "#fff",
			}}>
			<h2
				style={{
					marginBottom: "0.5rem",
					color: "#2d3748",
					textAlign: "center",
				}}>
				Adjust Matching Thresholds
			</h2>
			<p style={{ fontSize: 14, color: "#4a5568", marginBottom: "1.5rem" }}>
				Set the thresholds to control how matches are categorized:
				<br />
				<br />
				<strong style={{ color: "#e53e3e" }}>Duplicate (Red):</strong> Scores at
				or above this threshold are considered duplicates.
				<br />
				<strong style={{ color: "#d69e2e" }}>
					Needs Review (Yellow):
				</strong>{" "}
				Scores between this value and the duplicate threshold require manual
				review.
				<br />
				Scores below yellow threshold are considered non-duplicates (Green).
			</p>

			<form
				style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
				onSubmit={(e) => e.preventDefault()}>
				<label
					htmlFor="red-threshold"
					style={{ fontWeight: "600", color: "#e53e3e" }}>
					Duplicate Threshold (Red)
				</label>
				<input
					id="red-threshold"
					type="number"
					step="0.01"
					min="0"
					max="1"
					value={redThreshold}
					onChange={handleRedChange}
					placeholder="e.g., 0.7"
					style={{
						padding: "0.5rem 0.75rem",
						fontSize: 16,
						borderRadius: 6,
						border: "1.5px solid #cbd5e0",
						outlineOffset: 2,
						transition: "border-color 0.2s ease",
					}}
					onFocus={(e) => (e.target.style.borderColor = "#e53e3e")}
					onBlur={(e) => (e.target.style.borderColor = "#cbd5e0")}
				/>

				<label
					htmlFor="yellow-threshold"
					style={{ fontWeight: "600", color: "#d69e2e" }}>
					Needs Review Threshold (Yellow)
				</label>
				<input
					id="yellow-threshold"
					type="number"
					step="0.01"
					min="0"
					max="1"
					value={yellowThreshold}
					onChange={handleYellowChange}
					placeholder="e.g., 0.4"
					style={{
						padding: "0.5rem 0.75rem",
						fontSize: 16,
						borderRadius: 6,
						border: "1.5px solid #cbd5e0",
						outlineOffset: 2,
						transition: "border-color 0.2s ease",
					}}
					onFocus={(e) => (e.target.style.borderColor = "#d69e2e")}
					onBlur={(e) => (e.target.style.borderColor = "#cbd5e0")}
				/>
			</form>

			{warning && (
				<p
					style={{
						marginTop: 10,
						fontSize: 13,
						color: "#b83232",
						fontWeight: "600",
						userSelect: "none",
					}}>
					{warning}
				</p>
			)}
		</section>
	);
}
